import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Mail, Eye } from 'lucide-react';
import { format } from 'date-fns';
import DOMPurify from 'dompurify';
import ClientPortalLayout from '@/components/client/ClientPortalLayout';

interface SentEmail {
  id: string;
  recipient_email: string;
  subject: string;
  body_html: string;
  body_text: string;
  sent_at: string;
  status: string;
  email_templates?: {
    template_name: string;
  };
}

export default function EmailHistory() {
  const [selectedEmail, setSelectedEmail] = useState<SentEmail | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  const { data: sentEmails, isLoading } = useQuery({
    queryKey: ['client-sent-emails'],
    queryFn: async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Not authenticated');

      const { data: clientData } = await supabase
        .from('clients')
        .select('id')
        .eq('user_id', userData.user.id)
        .single();

      if (!clientData) throw new Error('Client not found');

      const { data, error } = await supabase
        .from('sent_emails')
        .select(`
          *,
          email_templates(template_name)
        `)
        .eq('client_id', clientData.id)
        .order('sent_at', { ascending: false });

      if (error) throw error;
      return data as SentEmail[];
    },
  });

  const handleViewEmail = (email: SentEmail) => {
    setSelectedEmail(email);
    setViewModalOpen(true);
  };

  return (
    <ClientPortalLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Email History</h1>
          <p className="text-muted-foreground">
            View all emails sent to you
          </p>
        </div>

        {isLoading ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              Loading email history...
            </CardContent>
          </Card>
        ) : sentEmails && sentEmails.length > 0 ? (
          <div className="grid gap-4">
            {sentEmails.map((email) => (
              <Card key={email.id} className="hover:border-primary/50 transition-colors">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <Mail className="h-5 w-5 text-primary mt-1" />
                      <div>
                        <CardTitle className="text-lg">{email.subject}</CardTitle>
                        <CardDescription>
                          {format(new Date(email.sent_at), 'MMMM d, yyyy at h:mm a')}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="secondary">
                      {email.email_templates?.template_name || 'Message'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      To: {email.recipient_email}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewEmail(email)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Email
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No emails found
            </CardContent>
          </Card>
        )}

        {selectedEmail && (
          <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{selectedEmail.subject}</DialogTitle>
                <DialogDescription>
                  Sent on {format(new Date(selectedEmail.sent_at), 'MMMM d, yyyy at h:mm a')}
                </DialogDescription>
              </DialogHeader>

              <div className="rounded-lg border p-6 bg-background">
                <div
                  dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize(selectedEmail.body_html, {
                      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'img', 'div', 'span', 'table', 'thead', 'tbody', 'tr', 'td', 'th'],
                      ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'title', 'class', 'style']
                    })
                  }}
                  className="prose prose-sm max-w-none"
                />
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </ClientPortalLayout>
  );
}
